import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import ConfirmModal from '../../components/ConfirmModal';
import { useNavigate, useParams } from 'react-router-dom';
import contaService from '../../services/contaService';

const initialState = {
  codigo: '',
  descricao: '',
  tipo: '',
  contabilizar: '',
  dataInclusao: '',
};

const ContaEdit = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    contaService.getById(id).then(conta => {
      if (conta) {
        setForm({
          codigo: conta.codigo || '',
          descricao: conta.descricao || '',
          tipo: conta.tipo || '',
          contabilizar: conta.contabilizar || '',
          dataInclusao: conta.dataInclusao || '',
        });
      }
      setLoading(false);
    });
  }, [id]);

  const validate = async () => {
    if (!form.codigo.trim()) return 'O código é obrigatório.';
    if (!form.descricao.trim()) return 'A descrição é obrigatória.';
    if (!form.tipo.trim()) return 'O tipo é obrigatório.';
    
    // Verificar se código já existe (exceto para o próprio registro)
    try {
      const contas = await contaService.list({ codigo: form.codigo });
      if (contas.contas && contas.contas.length > 0) {
        const existingConta = contas.contas.find(conta => conta.id.toString() !== id);
        if (existingConta) {
          return 'Já existe uma conta com este código.';
        }
      }
    } catch (error) {
      console.error('Erro ao verificar código:', error);
    }
    
    // Verificar se descrição já existe (exceto para o próprio registro)
    try {
      const contas = await contaService.list({ descricao: form.descricao });
      if (contas.contas && contas.contas.length > 0) {
        const existingConta = contas.contas.find(conta => conta.id.toString() !== id);
        if (existingConta) {
          return 'Já existe uma conta com esta descrição.';
        }
      }
    } catch (error) {
      console.error('Erro ao verificar descrição:', error);
    }
    
    return '';
  };

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    const validationError = await validate();
    if (validationError) {
      setError(validationError);
      setLoading(false);
      return;
    }
    setLoading(false);
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setLoading(true);
    setError('');
    setSuccess(false);
    try {
      await contaService.update(id, form);
      setLoading(false);
      setSuccess(true);
      setTimeout(() => navigate('/contas'), 1200);
    } catch (err) {
      setLoading(false);
      setError('Erro ao atualizar conta.');
    }
  };

  const handleDelete = async () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setDeleting(true);
    try {
      await contaService.delete(id);
      setDeleting(false);
      setDeleteSuccess(true);
      setTimeout(() => navigate('/contas'), 1200);
    } catch (err) {
      setDeleting(false);
      setError('Erro ao excluir conta.');
    }
  };

  if (loading) return <div className="max-w-lg mx-auto mt-10 text-blue-600">Carregando dados...</div>;
  if (deleteSuccess) return <div className="max-w-lg mx-auto mt-10 text-green-600">Conta excluída com sucesso!</div>;

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <h2 className="text-xl font-bold mb-4">Editar Conta</h2>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Código *</label>
          <input
            type="text"
            name="codigo"
            value={form.codigo}
            onChange={handleChange}
            maxLength={50}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Código da conta"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Descrição *</label>
          <input
            type="text"
            name="descricao"
            value={form.descricao}
            onChange={handleChange}
            maxLength={255}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
            placeholder="Descrição da conta"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tipo *</label>
          <select
            name="tipo"
            value={form.tipo}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="Entrada">Entrada</option>
            <option value="Saida">Saida</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Contabilizar *</label>
          <select
            name="contabilizar"
            value={form.contabilizar}
            onChange={handleChange}
            required
            className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-400"
          >
            <option value="Sim">Sim</option>
            <option value="Não">Não</option>
          </select>
        </div>
        {error && <div className="text-red-600 text-sm">{error}</div>}
        {loading && <div className="text-blue-600 text-sm">Salvando...</div>}
        {success && <div className="text-green-600 text-sm">Conta atualizada com sucesso!</div>}
        <div className="flex flex-row gap-1 justify-end mt-2 w-full overflow-x-auto">
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-gray-200 hover:bg-gray-300 text-gray-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={() => navigate('/contas')}>
            <FontAwesomeIcon icon={faTimes} />
            <span className="hidden sm:inline">Cancelar</span>
          </button>
          <button type="submit" className="p-2 sm:px-4 sm:py-2 rounded bg-green-600 text-white font-semibold hover:bg-green-700 flex items-center gap-2 w-10 sm:w-auto justify-center" aria-label="Salvar">
            <FontAwesomeIcon icon={faSave} />
            <span className="hidden sm:inline">Salvar</span>
          </button>
          <button type="button" className="p-2 sm:px-4 sm:py-2 rounded bg-red-600 text-white font-semibold hover:bg-red-700 flex items-center gap-2 w-10 sm:w-auto justify-center" onClick={handleDelete} disabled={deleting} aria-label="Excluir">
            <FontAwesomeIcon icon={faTrash} />
            <span className="hidden sm:inline">{deleting ? 'Excluindo...' : 'Excluir'}</span>
          </button>
        </div>
      </form>
      <ConfirmModal
        isOpen={showConfirm}
        title="Confirmar Edição"
        message="Deseja realmente salvar as alterações desta conta?"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
      />
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Confirmar Exclusão"
        message="Deseja realmente excluir esta conta? Esta ação não poderá ser desfeita."
        confirmType="delete"
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        danger
      />
    </div>
  );
};

export default ContaEdit;
